/**
 * Step 2 · Ciało i sprzęt — what the movement trains, and what it needs.
 *
 * Two columns, the specimen's `1.12fr 1fr`: the silhouettes beside a chip grid in four regions on
 * the left, the shared search field over twenty-four equipment tiles on the right. The muscles and
 * the equipment share a step by owner decision (D8, D11 — the round's premise was that a step of
 * its own for muscles held 328px of content in a 694px pane).
 *
 * ── The chip is Shopify's glyph swap ────────────────────────────────────────────────────────
 * `+ Name` → `✓ Name 1°` → the small degree button flips 1° / 2° → the body clears it. Every
 * transition is `model/muscleChipCycle`'s; the chip only draws the state and dispatches. The body is
 * a toggle (`aria-pressed`) and the degree button names the degree it is IN and what it does, so a
 * listener hears „Klatka Piersiowa: główne, zmień stopień" rather than „1°".
 *
 * The figure repaints live from the same two lists, and it is never the only channel for the
 * degree: the chips carry it in words, and in Flame Dark the two fills differ by more than
 * luminance (`--silhouette-strong` against `--silhouette-mid`). Labels are the mobile locale's,
 * Title Case, by owner decision D13.
 *
 * ── The equipment grid scrolls in its OWN box ───────────────────────────────────────────────
 * The canon puts the tiles in a 430px scroller so the step's own scroll and the footer do not move
 * when the search narrows them. They are the TAXONOMY (`GET /equipment`, most-used first), the
 * wizard's one read, so the box has a wait, a failure and an emptiness of its own. The search is `shared/ui/SearchField` (D12) — the
 * library's own field, not a second one — at its DEFAULT cut: the frozen PNG draws it 286 × 34, which
 * is that size, where the compact cut is the plan library's 27px pane control.
 */

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MUSCLE_REGIONS,
  MuscleFigures,
  muscleGroupLabelKey,
  type MuscleGroup,
} from '~/entities/exercise-library';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { DumbbellMarkIcon } from '~/shared/ui/EmptyStateIcons';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import photoBox from '~/shared/ui/photoBox.module.css';
import { SearchField } from '~/shared/ui/SearchField';
import { CheckGlyph } from '~/shared/ui/FormGlyphs';

import type { CreateExerciseState } from '../lib/useCreateExercise';
import type { EquipmentTile, EquipmentTilesView } from '../model/equipmentTiles';
import { muscleDegree } from '../model/muscleChipCycle';

import styles from './CreateExerciseDialog.module.css';

export function StepBody({ wizard }: { readonly wizard: CreateExerciseState }) {
  return (
    <div className={`${String(styles.cols)} ${String(styles.colsBody)}`}>
      <div className={styles.col}>
        <Muscles wizard={wizard} />
      </div>
      <div className={styles.col}>
        <Equipment wizard={wizard} />
      </div>
    </div>
  );
}

function Muscles({ wizard }: { readonly wizard: CreateExerciseState }) {
  const { t } = useTranslation();
  const { draft } = wizard;

  return (
    <div className={styles.block}>
      <span className={styles.label}>
        {t('web:exerciseLibrary.creator.muscles.label')}
        <span className={styles.required}>{t('web:exerciseLibrary.creator.required')}</span>
      </span>
      <div className={styles.mus}>
        {/* A press on a shape is the SAME transition as a press on that muscle's chip (D21). The
          * figure stays `aria-hidden`: the chips beside it are the keyboard and screen-reader path. */}
        <MuscleFigures
          primary={draft.primary}
          secondary={draft.secondary}
          size="creator"
          onPressMuscle={(group) => {
            wizard.dispatch({ type: 'cycleMuscle', group });
          }}
        />
        <div className={styles.regions}>
          {MUSCLE_REGIONS.map((region) => (
            <div key={region.key} className={styles.region}>
              <span className={styles.regionTitle}>
                {t(`web:exerciseLibrary.filters.regions.${region.key}`)}
              </span>
              <div className={styles.chips}>
                {region.groups.map((group) => (
                  <MuscleChip
                    key={group}
                    wizard={wizard}
                    group={group}
                    // The first chip of all is where a refused „Dalej” sends focus.
                    focusTarget={group === MUSCLE_REGIONS[0]?.groups[0]}
                  />
                ))}
              </div>
            </div>
          ))}
          <p className={styles.legend}>{t('web:exerciseLibrary.creator.muscles.legend')}</p>
        </div>
      </div>
    </div>
  );
}

function MuscleChip({
  wizard,
  group,
  focusTarget,
}: {
  readonly wizard: CreateExerciseState;
  readonly group: MuscleGroup;
  readonly focusTarget: boolean;
}) {
  const { t } = useTranslation();
  const degree = muscleDegree(wizard.draft, group);
  const name = t(muscleGroupLabelKey(group));

  return (
    <span
      className={[
        styles.chip,
        degree === 'primary' ? styles.chipPrimary : '',
        degree === 'secondary' ? styles.chipSecondary : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className={styles.chipBody}
        aria-pressed={degree !== null}
        data-blocker={focusTarget ? 'primaryMuscle' : undefined}
        onClick={() => {
          wizard.dispatch({ type: 'cycleMuscle', group });
        }}
      >
        <span className={styles.chipGlyph} aria-hidden>
          {degree === null ? '+' : <CheckGlyph className={styles.chipGlyphMark} />}
        </span>
        {name}
      </button>
      {degree !== null && (
        <button
          type="button"
          className={styles.chipDegree}
          aria-label={t(`web:exerciseLibrary.creator.muscles.${degree}`, { name })}
          onClick={() => {
            wizard.dispatch({ type: 'toggleMuscleDegree', group });
          }}
        >
          {degree === 'primary'
            ? t('exerciseLibrary.createWizard.musclesForm.primaryBadge')
            : t('exerciseLibrary.createWizard.musclesForm.secondaryBadge')}
        </button>
      )}
    </span>
  );
}

function Equipment({ wizard }: { readonly wizard: CreateExerciseState }) {
  const { t } = useTranslation();
  // The field stands in every state of the box, so it is where focus goes when „Spróbuj ponownie"
  // unmounts into the wait.
  const search = useRef<HTMLInputElement>(null);

  return (
    <div className={styles.block}>
      <span className={styles.label}>{t('web:exerciseLibrary.creator.equipment.label')}</span>
      <SearchField
        inputRef={search}
        value={wizard.equipmentSearch}
        onChange={wizard.setEquipmentSearch}
        label={t('web:exerciseLibrary.creator.equipment.search')}
        placeholder={t('web:exerciseLibrary.creator.equipment.search')}
        clearLabel={t('web:exerciseLibrary.clearSearch')}
        disabled={false}
      />
      <EquipmentBox
        wizard={wizard}
        onRetry={() => {
          wizard.retryEquipment();
          search.current?.focus();
        }}
      />
    </div>
  );
}

/** How many placeholder tiles the wait draws: three rows of four, more than the box shows. */
const SKELETON_TILES = Array.from({ length: 12 }, (_, index) => index);

/**
 * The tiles' box, in the five states `model/equipmentTiles` decides. The box is ONE fixed height in
 * all of them, so the answer landing changes ink and never geometry; the wait draws tile-shaped bars
 * inside it, gated (150ms grace, 300ms minimum life).
 *
 * The fork runs on `shown`, which is the model's verdict once the GATE says the wait is over rather
 * than once the read answers. The two differ by the skeleton's 300ms minimum life, and forking on
 * the read instead flashes the tiles in at full opacity and then blinks them out and back when the
 * hand-over finally arrives. `aria-busy` and the hidden sentence read `shown` as well, since
 * 21 September 2026: a box still full of bars is still about to change, so reporting it settled —
 * or naming its failure — the instant the read landed says it 300ms before it is true.
 */
function EquipmentBox({
  wizard,
  onRetry,
}: {
  readonly wizard: CreateExerciseState;
  readonly onRetry: () => void;
}) {
  const { t } = useTranslation();
  const view = wizard.equipment;
  const gate = useSkeletonGate(view.status === 'pending');
  const shown: EquipmentTilesView = gate.waiting ? { status: 'pending' } : view;

  return (
    <div className={styles.equipBox} aria-busy={shown.status === 'pending'}>
      {/* Always mounted, so the failure is announced: a status inserted with its text is one some
        * screen readers never read. The sentence below draws it for the eye. */}
      <p className={hidden.srOnly} role="status">
        {shown.status === 'error' ? t('exerciseLibrary.createWizard.equipment.loadError') : ''}
      </p>
      {shown.status === 'pending' ? (
        <div className={styles.equipGrid} aria-hidden>
          {gate.skeleton &&
            SKELETON_TILES.map((index) => (
              <span key={index} className={styles.equipTile}>
                <i className={`${String(skel.bar)} ${String(styles.equipSkelPlate)}`} />
                <i className={`${String(skel.bar)} ${String(styles.equipSkelLabel)}`} />
              </span>
            ))}
        </div>
      ) : shown.status === 'error' ? (
        /* Every answer takes the hand-over, not only the tiles: a failure, an empty catalogue and
         * a filter that matched nothing all replace the same bars. */
        <div className={`${String(styles.equipState)} ${gate.reveal}`}>
          <p className={styles.equipEmpty} data-equipment-error>
            {t('exerciseLibrary.createWizard.equipment.loadError')}
          </p>
          <button type="button" className={styles.textButton} onClick={onRetry}>
            {t('web:exerciseLibrary.states.retry')}
          </button>
        </div>
      ) : shown.status === 'empty' ? (
        <div className={`${String(styles.equipState)} ${gate.reveal}`}>
          <EmptyState
            inline
            mark={<DumbbellMarkIcon />}
            tone="neutral"
            title={t('web:exerciseLibrary.creator.equipment.empty')}
          />
        </div>
      ) : shown.status === 'noMatch' ? (
        <p className={`${String(styles.equipEmpty)} ${gate.reveal}`}>
          {t('exerciseLibrary.createWizard.equipment.noResults')}
        </p>
      ) : (
        <div className={`${String(styles.equipGrid)} ${gate.reveal}`}>
          {shown.tiles.map((tile) => (
            <EquipmentTileButton key={tile.piece.id} wizard={wizard} tile={tile} />
          ))}
        </div>
      )}
    </div>
  );
}

function EquipmentTileButton({
  wizard,
  tile,
}: {
  readonly wizard: CreateExerciseState;
  readonly tile: EquipmentTile;
}) {
  const { piece } = tile;
  // A failed `<img alt="">` is Chrome's broken-file mark, not nothing, so the letter takes over.
  const [failed, setFailed] = useState(false);

  return (
    <button
      type="button"
      className={styles.equipTile}
      aria-pressed={tile.selected}
      onClick={() => {
        wizard.dispatch({ type: 'toggleEquipment', equipmentId: piece.id });
      }}
    >
      <span className={styles.equipPlate}>
        {piece.imageUrl === null || failed ? (
          <i className={styles.equipLetter} aria-hidden>
            {piece.name.slice(0, 1).toUpperCase()}
          </i>
        ) : (
          <img
            className={`${String(photoBox.photo)} ${String(styles.equipPhoto)}`}
            src={piece.imageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            onError={() => {
              setFailed(true);
            }}
          />
        )}
        <span className={styles.equipCheck} aria-hidden>
          <CheckGlyph className={styles.equipCheckGlyph} />
        </span>
      </span>
      <span className={styles.equipLabel}>{piece.name}</span>
    </button>
  );
}
